
import { toast } from "sonner";
import { marked } from "marked";
import { useCreateDocument } from "../../hooks/useCreateDocument";
import { useClipboard } from "../../hooks/useClipboard";
import { convertHtmlToSfdt } from "../../services/document.service";

export interface UseMessageActionsOptions {
  documentId?: string;
  documentTitle?: string;
}

export const useMessageActions = (options: UseMessageActionsOptions = {}) => {
  const { mutateAsync: createDoc, isPending: isCreatingDocument } = useCreateDocument();
  const { copyHtmlToClipboard } = useClipboard();

  // Handler to copy message
  const handleCopyMessage = async (messageText: string) => {
    const htmlContent = await marked(messageText);
    await copyHtmlToClipboard(htmlContent, messageText, "Message copied to clipboard");
  };

  // Handler to insert message into document
  const handleInsertMessageToDocument = async (messageText: string) => {
    try {
      // Convert markdown to HTML using marked library
      const htmlContent = await marked(messageText);

      await Word.run(async (context) => {
        const selection = context.document.getSelection();
        // Use insertHtml instead of insertParagraph to support rich formatting
        selection.insertHtml(htmlContent, Word.InsertLocation.replace);
        await context.sync();
      });

      toast.success("Inserted!", {
        description: "Message inserted into document",
      });
    } catch (err) {
      console.error("Failed to insert message:", err);
      toast.error("Insert Failed", {
        description: "Failed to insert message into document",
      });
    }
  };

  // Handler to create document from message
  const handleCreateDocumentFromMessage = async (messageText: string, customTitle?: string) => {
    if (!options.documentId || !options.documentTitle) {
      toast.error("Error", {
        description: "Document information not available",
      });
      return;
    }

    try {
      // Convert markdown to HTML
      const htmlContent = await marked(messageText);

      // Convert HTML to SFDT
      const sfdtContent = await convertHtmlToSfdt(htmlContent);

      // Generate random editor ID (similar to front-legal)
      const editorId =
        Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);

      const title = customTitle || `${options.documentTitle}-AI-Discussion`;

      // Create document
      const result = await createDoc({
        editor_id: editorId,
        title: title,
        content: sfdtContent,
        collaborators: [],
        folderId: null,
      });

      toast.success("Success!", {
        description: `Document created successfully. Document ID: ${result.document_id}`,
      });

      if (result.document_id) {
        window.open(`${process.env.APP_FRONTEND_URL}/d/${result.document_id}`, "_blank");
      }
    } catch (error) {
      console.error("Failed to create document:", error);
      toast.error("Creation Failed", {
        description: "Failed to create document. Please try again.",
      });
    }
  };

  // Handler to save message to bank
  // For Word add-in, we save to a custom property that can be accessed later
  const handleSaveToBank = async (messageText: string) => {
    try {
      await Word.run(async (context) => {
        // Get or create a custom property to store "banked" content
        const properties = context.document.properties.customProperties;
        properties.load("items");
        await context.sync();

        // Generate a unique key for this banked item
        const timestamp = new Date().getTime();
        const key = `bank_item_${timestamp}`;

        // Save the content
        properties.add(key, messageText);
        await context.sync();
      });

      toast.success("Saved to Bank!", {
        description: "Message saved successfully and can be reused later",
      });
    } catch (err) {
      console.error("Failed to save to bank:", err);
      toast.error("Save Failed", {
        description: "Failed to save message to bank",
      });
    }
  };

  return {
    handleCopyMessage,
    handleInsertMessageToDocument,
    handleCreateDocumentFromMessage,
    handleSaveToBank,
    isCreatingDocument,
  };
};
